import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Store, Plus, ArrowDownToLine, Trash2, Loader2, BadgeCheck, QrCode, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { buildQrPayload } from "@/lib/qrpay";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/formatError";
import { toast } from "sonner";

const SYMBOL = { EUR: "\u20ac", USD: "$" };

export const MerchantSection = ({ cards = [] }) => {
  const [merchants, setMerchants] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [withdrawFor, setWithdrawFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [receiveFor, setReceiveFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/merchants/me");
      setMerchants(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="merchant-section"
      className="mb-6 rounded-2xl border border-[#FFE600]/25 bg-[#FFE600]/[0.04] p-5"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-[#FFE600]" />
          <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB]">
            Merchant profiles · separate balance
          </span>
        </div>
        <Button
          data-testid="become-merchant-button"
          size="sm"
          onClick={() => setCreateOpen(true)}
          className="rounded-full bg-[#FFE600] text-black hover:bg-[#FFE600]/90 h-9 font-semibold"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Become a merchant
        </Button>
      </div>

      {merchants.length === 0 ? (
        <p className="text-sm text-[#A0A0AB]" data-testid="no-merchants">
          You have no merchant profiles yet. Claim a unique name so payments can be wired to you.
        </p>
      ) : (
        <div className="space-y-3" data-testid="merchant-list">
          {merchants.map((m) => (
            <div
              key={m.id}
              data-testid={`merchant-${m.id}`}
              className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex-wrap"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-mono-sig text-[#FFE600] text-sm">{m.name}</p>
                  <span
                    data-testid={`merchant-reality-${m.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-[#00FF9D]/10 border border-[#00FF9D]/40 px-2 py-0.5"
                  >
                    <BadgeCheck className="w-3 h-3 text-[#00FF9D]" strokeWidth={2.5} />
                    <span className="font-mono-sig text-[8px] tracking-[0.2em] uppercase text-[#00FF9D]">
                      {m.reality || "Real"}
                    </span>
                  </span>
                  <span className="font-mono-sig text-[8px] tracking-[0.2em] uppercase text-[#00C2FF] bg-[#00C2FF]/10 border border-[#00C2FF]/30 rounded-full px-2 py-0.5">
                    {m.currency || "EUR"}
                  </span>
                </div>
                <p className="font-mono-sig text-lg font-black text-white">{SYMBOL[m.currency] || "\u20ac"}{Number(m.balance).toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid={`merchant-receive-${m.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => setReceiveFor(m)}
                  className="rounded-full h-9 border-[#00C2FF]/40 bg-[#00C2FF]/10 text-[#00C2FF] hover:bg-[#00C2FF]/20"
                >
                  <QrCode className="w-4 h-4 mr-1.5" /> Receive
                </Button>
                <Button
                  data-testid={`merchant-withdraw-${m.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => setWithdrawFor(m)}
                  className="rounded-full h-9 border-[#00FF9D]/40 bg-[#00FF9D]/10 text-[#00FF9D] hover:bg-[#00FF9D]/20"
                >
                  <ArrowDownToLine className="w-4 h-4 mr-1.5" /> Withdraw
                </Button>
                <Button
                  data-testid={`merchant-delete-${m.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteFor(m)}
                  className="rounded-full h-9 border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateMerchantDialog open={createOpen} onOpenChange={setCreateOpen} onDone={load} />
      <WithdrawDialog merchant={withdrawFor} cards={cards} onOpenChange={(o) => !o && setWithdrawFor(null)} onDone={load} />
      <DeleteDialog merchant={deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)} onDone={load} />
      <ReceiveDialog merchant={receiveFor} onOpenChange={(o) => !o && setReceiveFor(null)} />
    </motion.div>
  );
};

const ReceiveDialog = ({ merchant, onOpenChange }) => {
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");

  useEffect(() => { setAmount(""); setReference(""); }, [merchant]);

  const credentials = merchant
    ? buildQrPayload({ iban: merchant.iban || merchant.name, amount: amount || null, currency: merchant.currency, reference })
    : "";

  const copyCreds = () => {
    navigator.clipboard?.writeText(credentials);
    toast.success("Payment credentials copied");
  };
  return (
    <Dialog open={!!merchant} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0C] border-white/10 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle>Receive to {merchant?.name}</DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Share these payment credentials. The payer reads them in “Pay code / Take”. Set an amount
            to lock it, or leave blank for an open request. Funds land in your {merchant?.currency || "EUR"} balance.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-[#52525B] uppercase tracking-wider mb-1">Amount ({merchant?.currency})</label>
              <Input
                data-testid="merchant-receive-amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="open"
                className="bg-white/5 border-white/10 text-white h-10 font-mono-sig"
              />
            </div>
            <div>
              <label className="block text-[10px] text-[#52525B] uppercase tracking-wider mb-1">Reference</label>
              <Input
                data-testid="merchant-receive-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Invoice 1042"
                className="bg-white/5 border-white/10 text-white h-10"
              />
            </div>
          </div>
          <div className="rounded-xl border border-[#00C2FF]/25 bg-[#00C2FF]/[0.05] p-3">
            <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-[#A0A0AB] mb-1.5">Payment credentials</p>
            <p data-testid="merchant-receive-credentials" className="font-mono-sig text-xs text-[#00F0FF] break-all">{credentials}</p>
          </div>
          <Button data-testid="merchant-copy-credentials" onClick={copyCreds} className="w-full rounded-xl bg-[#00C2FF] text-black hover:bg-[#00C2FF]/90 h-10 font-semibold">
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy payment credentials
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CreateMerchantDialog = ({ open, onOpenChange, onDone }) => {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || password.length < 4) {
      toast.error("Pick a name and a password of at least 4 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/merchants", { name: name.trim(), password, currency });
      toast.success("Merchant profile created");
      setName("");
      setPassword("");
      setCurrency("EUR");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0C] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Which merchant are you?</DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Pick a unique network name. Payments wired to this name land in your merchant balance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-[#A0A0AB] mb-1.5">Merchant name</label>
            <Input
              data-testid="merchant-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="@mystore"
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
          <div>
            <label className="block text-xs text-[#A0A0AB] mb-1.5">Merchant password</label>
            <Input
              data-testid="merchant-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
          <div>
            <label className="block text-xs text-[#A0A0AB] mb-1.5">Receiving currency</label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="merchant-currency-select" className="bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-white/10 text-white">
                <SelectItem value="EUR">EUR · €</SelectItem>
                <SelectItem value="USD">USD · $</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="merchant-create-submit"
            onClick={submit}
            disabled={busy}
            className="w-full rounded-full bg-[#FFE600] text-black hover:bg-[#FFE600]/90 font-semibold h-11"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Claim name"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const WithdrawDialog = ({ merchant, cards, onOpenChange, onDone }) => {
  const [amount, setAmount] = useState("");
  const [cardId, setCardId] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (merchant && cards.length) setCardId(cards[0].id);
  }, [merchant, cards]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (!cardId) return toast.error("Pick a destination card");
    if (!password) return toast.error("Enter your merchant password");
    setBusy(true);
    try {
      await api.post(`/merchants/${merchant.id}/withdraw`, { amount: amt, to_card_id: cardId, password });
      toast.success("Withdrawn to card");
      setAmount("");
      setPassword("");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!merchant} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0C] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Withdraw from {merchant?.name}</DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Move funds from your merchant balance ({SYMBOL[merchant?.currency] || "\u20ac"}{Number(merchant?.balance || 0).toFixed(2)}) into one of your cards.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-[#A0A0AB] mb-1.5">Amount (USD)</label>
            <Input
              data-testid="withdraw-amount-input"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
          <div>
            <label className="block text-xs text-[#A0A0AB] mb-1.5">To card</label>
            <Select value={cardId} onValueChange={setCardId}>
              <SelectTrigger data-testid="withdraw-card-select" className="bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Select a card" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-white/10 text-white">
                {cards.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nickname || `•••• ${c.card_number.slice(-4)}`} · ${Number(c.balance).toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs text-[#A0A0AB] mb-1.5">Merchant password</label>
            <Input
              data-testid="withdraw-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
          <Button
            data-testid="withdraw-submit"
            onClick={submit}
            disabled={busy}
            className="w-full rounded-full bg-[#00FF9D] text-black hover:bg-[#00FF9D]/90 font-semibold h-11"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Withdraw"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeleteDialog = ({ merchant, onOpenChange, onDone }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!password) return toast.error("Enter your merchant password");
    setBusy(true);
    try {
      await api.delete(`/merchants/${merchant.id}`, { data: { password } });
      toast.success("Merchant profile deleted");
      setPassword("");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!merchant} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0C] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Delete {merchant?.name}?</DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            The balance must be $0 first. Enter your merchant password to confirm.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            data-testid="delete-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Merchant password"
            className="bg-white/5 border-white/10 text-white"
          />
          <Button
            data-testid="delete-submit"
            onClick={submit}
            disabled={busy}
            className="w-full rounded-full bg-[#FF007F] text-white hover:bg-[#FF007F]/90 font-semibold h-11"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete profile"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
